import React, { useState, useCallback } from 'react';
import Kbd from 'design-system/Kbd';
import { makeStyles } from 'design-system/styles';
import { Button } from 'design-system/Button';
import Tooltip from 'design-system/Tooltip';
import { validateAttribute } from '@scale/shared/annotation';
import { convertNumbersInput } from './utils';
import cx from 'classnames';
import _ from 'lodash';

interface Props {
  annotationId: string;
  isReadOnly?: boolean;
  attrParams: $FIXME;
  attrKey: string;
  attrValue?: string | number[];
  handleUpdateAttrValue: (attrKey: string, attrVal: number[]) => void;
  allowedNumbers?: number[];
}

const AttributeNumberArrayInput = (props: Props) => {
  const classes = useStyles();

  const { attrParams, isReadOnly, attrKey, attrValue, handleUpdateAttrValue, allowedNumbers } =
    props;

  // If params.is_frame_indices, then displayed number = actual number + 1 because frame number starts from 1 on the tasking UI
  const isFrameIndices = _.get(attrParams, 'is_frame_indices', false);

  const numberArray = convertNumbersInput(attrValue);

  const [newNumber, setNewNumber] = useState<string>('');

  const isAllowedNumber =
    allowedNumbers === undefined
      ? true
      : (allowedNumbers || []).includes(_.toNumber(newNumber) - 1);
  const isNewNumberValid = isAllowedNumber && validateAttribute(newNumber, attrParams, null);

  const handleAddNumber = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isNewNumberValid && e.key === 'Enter') {
        let newAttrValue = [
          ...numberArray,
          _.toNumber(isFrameIndices ? +newNumber - 1 : newNumber),
        ];
        if (isFrameIndices) {
          newAttrValue = _.sortBy(newAttrValue);
          newAttrValue = _.sortedUniq(newAttrValue);
        }
        handleUpdateAttrValue(attrKey, newAttrValue);
        setNewNumber('');
      }
    },
    [isFrameIndices, isNewNumberValid, newNumber, numberArray, handleUpdateAttrValue, attrKey],
  );

  const handleDeleteNumber = useCallback(
    (index: number) => {
      const newNumberArray = [...numberArray];
      newNumberArray.splice(index, 1);
      handleUpdateAttrValue(attrKey, newNumberArray);
    },
    [attrKey, numberArray, handleUpdateAttrValue],
  );

  return (
    <div className={classes.container}>
      <div className={classes.numbersContainer}>
        {numberArray.map((num, index) => (
          <Tooltip key={index} placement="top" title="remove">
            <div className={classes.number} onClick={() => handleDeleteNumber(index)}>
              <div className={classes.center}>{isFrameIndices ? num + 1 : num}</div>
            </div>
          </Tooltip>
        ))}
      </div>
      <div className={classes.inputContainer}>
        <input
          className={cx(classes.inputField, isNewNumberValid && classes.validInput)}
          type="number"
          value={newNumber}
          onChange={e => {
            if (!isReadOnly) {
              setNewNumber(e.target.value);
            }
          }}
          onKeyDown={handleAddNumber}
        />
        <div>
          <Kbd>Enter</Kbd> <strong>to add</strong>
        </div>
      </div>
      {isFrameIndices && (
        <Tooltip
          title={'Set to all frames in which both the linked from and linked to annotations exist'}
          placement="bottom"
        >
          <div style={{ display: 'inline-block' }}>
            <Button
              variant="contained"
              data-for="frames-helper"
              onClick={() => {
                handleUpdateAttrValue(attrKey, allowedNumbers || []);
              }}
            >
              Reset to shared frames
            </Button>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {},
  numbersContainer: {
    border: `1px solid ${theme.colors.gray10}`,
    padding: '4px',
    maxHeight: '200px',
    overflow: 'scroll',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  inputField: {
    boxShadow: 'none',
    borderRadius: '4px',
    border: '1px solid #ccc',
    width: '70px',
    fontWeight: 'bold',
    outline: 'none',
    height: '36px',
    margin: '8px 12px 8px 0px',
    padding: '2px 8px',

    '&:focus': {
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid var(--color-primary)',
    },
  },
  validInput: {
    color: 'var(--color-primary)',
  },
  number: {
    display: 'inline-block',
    margin: '2px',
    padding: '4px',
    backgroundColor: theme.colors.gray10,
    borderRadius: '2px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gold,
    },
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyItems: 'center',
  },
}));

export default AttributeNumberArrayInput;
